import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { postAsk } from "@/api/meeting-l10";
import { getMeeting, listMeetings, listSuggestions, type InRoom, type Meeting, type MeetingSuggestion } from "@/api/meetings";
import { useTrayLiveVoice } from "@/components/overview/cos-morning/cos-live-voice";
import { elapsedClockLabel } from "@/components/ring-central/ongoing-call-indicator";
import { useAuth } from "@/contexts/auth-context";
import { useCapabilities } from "@/hooks/use-capabilities";
import { apiErrorMessage } from "@/lib/api";
import { acquireTabLock, onTrayMessage, postTrayMessage, queryTabLock, useSharedTrayState } from "@/lib/tray-sync";

import { useMeetingCapture, type MeetingAnchor, type MeetingCapture } from "./capture";
import { patchMeeting, startMeeting, stopMeeting, type StartMeetingBody } from "./meeting-api";
import { useRefreshSuggestionsOnNotes } from "./use-refresh-on-notes";

/**
 * Where the tray's meeting is. `rest`: Meeting mode, nothing recording (Listen waits). `sheet`: the
 * start sheet is open. `recording`: capture runs (or is paused). `interrupted`: a reload ended capture
 * while the meeting still records and no other tab records it; Resume Recording picks it back up.
 * `after`: stopped, notes on the way.
 */
export type MeetingPhase = "rest" | "sheet" | "starting" | "recording" | "interrupted" | "stopping" | "after";

/**
 * Which of the user's tabs records. `recorder`: capture runs in this tab. `follower`: another tab of
 * this browser records; this one mirrors it, and its Stop asks that tab.
 */
export type MeetingRole = "recorder" | "follower";

export interface MeetingMode {
  /** Holds `kory.meeting_record`; without it the gear menu has no Meeting mode item and there is no Meeting mode. */
  capable: boolean;
  mode: "personal" | "meeting";
  /** Meeting ends Talk first; Personal while recording asks to stop instead. */
  setMode: (mode: "personal" | "meeting") => void;
  phase: MeetingPhase;
  /** A meeting is on: starting, recording, interrupted or stopping. */
  live: boolean;
  /** Who records a meeting in `recording` or `stopping`; null otherwise. */
  role: MeetingRole | null;
  /** Recording is paused, in this tab or in the recording tab. */
  paused: boolean;
  /** In `after`, when another tab or device stopped the meeting; null when this tab did. */
  stoppedBy: "another-tab" | "another-device" | null;
  meeting: Meeting | null;
  capture: MeetingCapture;
  error: string | null;
  openSheet: () => void;
  closeSheet: () => void;
  start: (opts: { title: string; inRoom: InRoom }) => Promise<void>;
  /**
   * The L10 runner's Record: starts a team meeting for its session on the tray's one capture, in
   * Meeting mode. Call it from the click (Chrome's tab picker needs the gesture). A no-op while a
   * meeting is already on.
   */
  startTeamMeeting: (opts: { teamId: string; l10SessionId: string; title?: string; inRoom: InRoom }) => Promise<void>;
  /** The runner's active headline or issue, stamped on each utterance from now on; null between them. */
  setAnchor: (anchor: MeetingAnchor) => void;
  /**
   * Takes a newer copy of the tray's meeting, such as the runner's Attach response once it has made
   * the running recording this L10's team meeting. Ignored for any other meeting.
   */
  adoptMeeting: (meeting: Meeting) => void;
  resumeRecording: () => Promise<void>;
  /** Stop waits for a second tap: an accidental one never ends a meeting. */
  stopAsked: boolean;
  askStop: () => void;
  cancelStop: () => void;
  stop: () => Promise<void>;
  setInRoom: (inRoom: InRoom) => void;
  /** Appends a timestamped line to the meeting's notes; false when it didn't save. */
  addNote: (text: string, ask?: NoteAsk) => Promise<boolean>;
  /** Back to Meeting mode at rest once the notes link has been seen. */
  done: () => void;
}

const INERT: MeetingMode = {
  capable: false,
  mode: "personal",
  setMode: () => {},
  phase: "rest",
  live: false,
  role: null,
  paused: false,
  stoppedBy: null,
  meeting: null,
  capture: {
    status: "idle",
    error: null,
    meetingId: null,
    elapsedMs: 0,
    levels: { mic: 0, tab: 0 },
    channels: { mic: { live: "off", audio: "off" }, tab: { live: "off", audio: "off" } },
    tabShared: false,
    lines: [],
    liveSaving: true,
    start: async () => {},
    pause: () => {},
    resume: () => {},
    stop: async () => {},
    setAnchor: () => {},
    setInRoom: () => {},
    tellKory: { holding: false, press: () => {}, release: async () => null },
  },
  error: null,
  openSheet: () => {},
  closeSheet: () => {},
  start: async () => {},
  startTeamMeeting: async () => {},
  setAnchor: () => {},
  adoptMeeting: () => {},
  resumeRecording: async () => {},
  stopAsked: false,
  askStop: () => {},
  cancelStop: () => {},
  stop: async () => {},
  setInRoom: () => {},
  addNote: async () => false,
  done: () => {},
};

const MeetingModeContext = createContext<MeetingMode>(INERT);

/** The tray's Meeting mode; inert (Personal, not capable) outside the app shell. */
export function useMeetingMode(): MeetingMode {
  return useContext(MeetingModeContext);
}

/** How long Stop waits for its second tap. */
const STOP_CONFIRM_MS = 4000;
/** How long Stop waits for a note still saving before it stops the meeting anyway. */
const NOTES_FLUSH_MS = 5000;
/** How long a follower's Stop waits for the recording tab to answer before it stops the meeting on the server. */
const STOP_RELAY_MS = 6000;
/** Once the recording tab has answered: its notes flush and capture's last uploads, with room to spare. */
const STOP_ACKED_MS = 30_000;

/** The lock the recording tab holds while its capture runs; the browser frees it when the tab goes. */
function recorderLock(meetingId: string): string {
  return `kory-meeting-recorder:${meetingId}`;
}

/** What one tab tells the others about the meeting. */
type MeetingMessage = {
  topic: "meeting";
  event: "started" | "paused" | "stopped" | "done" | "stop-request" | "stopping" | "sync-request";
  meetingId: string;
  paused?: boolean;
};

function tellTabs(event: MeetingMessage["event"], meetingId: string, extra: { paused?: boolean } = {}): void {
  postTrayMessage({ topic: "meeting", event, meetingId, ...extra } satisfies MeetingMessage);
}

/** The note as it lands in the meeting's notes: its time into the meeting, then the words. */
/** What a saved note also offers Kory as an ask: the typed line, or a held Tell Kory window's words. */
export interface NoteAsk {
  source: "typed" | "tell_kory";
  text: string;
}

/**
 * Hands a note to the ask detector without waiting on it. The server decides whether the line is an
 * ask (a typed line needs a "to-do:" style prefix), and a failure never touches the note's own save.
 */
function sendAsk(meetingId: string, ask: NoteAsk, anchor: MeetingAnchor): void {
  if (!ask.text.trim()) return;
  postAsk(meetingId, {
    text: ask.text.trim().slice(0, 4000),
    source: ask.source,
    anchor_type: anchor?.type ?? null,
    anchor_id: anchor?.id ?? null,
  }).catch(() => undefined);
}

export function noteLine(text: string, startedAt: string, now: Date): string {
  return `- [${elapsedClockLabel(new Date(startedAt), now)}] ${text.trim()}`;
}

const MEDIA_FAILED = "Kory couldn't reach your microphone or the tab. Try again.";
const LOST_TAIL = "The last few seconds may not have saved. Everything before them is in the notes.";

/** `followed`: another tab took the new meeting's recorder lock first, so this tab follows it. */
type StartPending = {
  op: "start";
  kind: "adhoc" | "team" | "resume";
  created: Meeting | null;
  createError: unknown;
  followed?: boolean;
};
/** The meeting's room and notes as the server last saved them, and the queue of writes to each. */
type Writes = {
  id: string;
  chains: Record<"room" | "notes", Promise<unknown>>;
  room: InRoom;
  notes: string | null;
  /** Note saves in flight (with their note), which Stop cancels if they outlast its wait. */
  noteSaves: Map<AbortController, UnsavedNote>;
};
/** A note by its id, so a separate note with the same words never stands in for it. */
type UnsavedNote = { id: string; text: string };
/** A capture start or stop the tray is waiting on; `remote`: the server copy, stopped elsewhere. */
type Pending = StartPending | { op: "stop"; meeting: Meeting; remote?: Meeting };

/**
 * The tab's one meeting, under both trays, so a recording survives moving between pages (a reload
 * ends capture; the meeting stays recording and offers Resume Recording). Talk and Meeting never
 * run together: Meeting mode ends a Talk call through its public stop. Across the user's tabs, one
 * records (it holds the recorder lock) and the rest follow it, kept in step by the tray channel and
 * the server's status.
 */
export function MeetingModeProvider({ children }: { children: ReactNode }) {
  const { can } = useCapabilities();
  const capable = can("kory.meeting_record");
  const { user } = useAuth();
  const voice = useTrayLiveVoice();
  const capture = useMeetingCapture();
  const queryClient = useQueryClient();
  const [mode, setModeState] = useSharedTrayState<"personal" | "meeting">("mode", "personal");
  const [phase, setPhase] = useState<MeetingPhase>("rest");
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [role, setRole] = useState<MeetingRole | null>(null);
  const [remotePaused, setRemotePaused] = useState(false);
  const [stoppedBy, setStoppedBy] = useState<MeetingMode["stoppedBy"]>(null);
  const [error, setError] = useState<string | null>(null);
  const [stopAsked, setStopAsked] = useState(false);
  const meetingRef = useRef(meeting);
  meetingRef.current = meeting;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const roleRef = useRef(role);
  roleRef.current = role;
  const roomSeq = useRef(0);
  // The meeting's write queues and the notes they build on (see `queueWrite`).
  const writes = useRef<Writes | null>(null);
  // The meeting whose notes card was closed, here or in another tab; a late read never reopens it.
  const dismissed = useRef<string | null>(null);

  // Capture reports how a start or stop went through its status, not by throwing; the tray notes
  // what it is waiting on and settles it once capture's status says.
  const [pending, setPending] = useState<Pending | null>(null);

  // The recorder lock this tab holds, released when its capture ends or the tray unmounts.
  const lockRef = useRef<{ id: string; release: () => void } | null>(null);
  const releaseLock = useCallback(() => {
    lockRef.current?.release();
    lockRef.current = null;
  }, []);
  useEffect(() => releaseLock, [releaseLock]);
  // A lock that arrives after the tray unmounted is let go at once: no controls would release it.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A meeting recording somewhere: followed when another tab here holds its lock, otherwise waiting
  // on Resume Recording. Skipped once this tab has moved on (started, stopped or recorded a meeting).
  const placeRecording = useCallback(
    async (latest: Meeting) => {
      const held = (await queryTabLock(recorderLock(latest.id))) === "held";
      // Checked when applied, after the lock read: a meeting that has since stopped, or whose card
      // was closed, is never placed.
      if (latest.status !== "recording" || dismissed.current === latest.id) return;
      const current = meetingRef.current;
      const p = phaseRef.current;
      const open = current
        ? current.id === latest.id
          ? p === "interrupted" || (p === "recording" && roleRef.current === "follower")
          : p === "after"
        : p === "rest" || p === "sheet" || p === "after";
      if (!open) return;
      // Only when first adopting the meeting: a follower's refresh never overrides a mode chosen since.
      if (!current || current.id !== latest.id) setModeState("meeting");
      setStoppedBy(null);
      if (held) {
        // A follower shows the server's notes, which only the recording tab writes; a later note
        // from this tab builds on them, not on what it saved before the other tab took over.
        writes.current = null;
        setMeeting(latest);
        setRole("follower");
        setPhase("recording");
        return;
      }
      if (!current || current.id !== latest.id || roleRef.current === "follower") {
        writes.current = null;
        setMeeting(latest);
      }
      setRole(null);
      setRemotePaused(false);
      setPhase("interrupted");
    },
    [setModeState],
  );

  // Another tab or device stopped the meeting this tab followed or waited on.
  const showStopped = useCallback(
    (latest: Meeting, by: MeetingMode["stoppedBy"]) => {
      // A late read for a meeting this tab has moved on from changes nothing.
      if (meetingRef.current && meetingRef.current.id !== latest.id) return;
      if (dismissed.current === latest.id) {
        setMeeting(null);
        setRole(null);
        setRemotePaused(false);
        setStoppedBy(null);
        setPhase("rest");
        return;
      }
      setMeeting(latest);
      setRole(null);
      setRemotePaused(false);
      setStopAsked(false);
      setStoppedBy(by);
      setPhase("after");
      void queryClient.invalidateQueries({ queryKey: ["meetings"] });
    },
    [queryClient],
  );

  // Notes still saving land before capture stops: once the meeting is processing, a late write would
  // replace the notes Kory composes. A stalled save never holds a stop up for long: it is cancelled,
  // so it can't reach the server after processing has finished, and its words are kept to copy.
  const flushNotes = useCallback(async (meetingId: string) => {
    if (writes.current?.id !== meetingId) return;
    const w = writes.current;
    await Promise.race([w.chains.notes, new Promise((resolve) => setTimeout(resolve, NOTES_FLUSH_MS))]);
    // Kept across a retried Stop: a failed stop must not drop notes an earlier attempt cancelled.
    const cancelled = [...w.noteSaves.values()];
    unsavedNotes.current = [...unsavedNotes.current.filter((n) => !cancelled.some((c) => c.id === n.id)), ...cancelled];
    w.noteSaves.forEach((_note, save) => save.abort());
  }, []);
  // The notes a stop had to cancel, as the line asking to copy them; clears them and the failed notes.
  const takeLostNotes = useCallback((): string | null => {
    const unsaved = unsavedNotes.current;
    unsavedNotes.current = [];
    failedTyped.current = null;
    failedTold.current = [];
    return unsaved.length
      ? `${unsaved.length === 1 ? "A note" : "Some notes"} didn't save before Stop. Copy ${unsaved.length === 1 ? "it" : "them"}: ${unsaved.map((n) => `“${n.text}”`).join(" ")}`
      : null;
  }, []);

  // After a reload: your own meeting still recording comes back, waiting on Resume Recording. The
  // lookup retries like any query, so one failed request doesn't strand the meeting's controls. Once
  // per signed-in user, read fresh each time.
  const recoveredFor = useRef<string | null>(null);
  const ownRecording = useQuery({
    queryKey: ["meetings", "own-recording", user?.id],
    queryFn: async () => (await listMeetings()).find((m) => m.status === "recording" && m.owner_user_id === user?.id) ?? null,
    enabled: capable && Boolean(user?.id) && recoveredFor.current !== user?.id,
    staleTime: 0,
    gcTime: 0,
  });
  useEffect(() => {
    if (!user?.id || recoveredFor.current === user.id || ownRecording.data === undefined) return;
    recoveredFor.current = user.id;
    const mine = ownRecording.data;
    if (!mine || meetingRef.current) return;
    void placeRecording(mine);
  }, [ownRecording.data, user?.id, placeRecording]);

  // The meeting's status on the server, so every tab agrees with it: while it records (each tab checks
  // whether the recording tab is still there, or whether it was stopped elsewhere), and once stopped,
  // until the notes land. Not while this tab is starting or stopping it.
  const after = phase === "after" && meeting ? meeting.id : null;
  const watched = meeting && (phase === "recording" || phase === "interrupted" || phase === "after") ? meeting.id : null;
  const status = useQuery({
    queryKey: ["meetings", watched, "detail"],
    queryFn: () => getMeeting(watched as string),
    enabled: Boolean(watched),
    // Once stopped, only a final answer ends the checks: a copy cached before Stop, or a failed read, keeps them going.
    refetchInterval: (q) =>
      phase !== "after" ? 5_000 : q.state.data?.status === "ready" || q.state.data?.status === "failed" ? false : 10_000,
    refetchOnWindowFocus: true,
    meta: { expectedErrorStatuses: [404] },
  });
  const captureStop = useRef(capture.stop);
  captureStop.current = capture.stop;
  useEffect(() => {
    const latest = status.data;
    const current = meetingRef.current;
    if (!latest || !current || latest.id !== current.id) return;
    const p = phaseRef.current;
    if (p === "after") {
      // A stopped meeting never records again: a "recording" answer is the copy cached before Stop.
      if (latest.status !== "recording") setMeeting(latest);
      return;
    }
    if (p !== "recording" && p !== "interrupted") return;
    if (latest.status === "recording") {
      if (roleRef.current !== "recorder") void placeRecording(latest);
      return;
    }
    // Stopped from another device while this tab records: end capture here too (its last writes are
    // refused), after the same note flush as Stop.
    if (roleRef.current === "recorder") {
      setPhase("stopping");
      void (async () => {
        await flushNotes(current.id);
        await captureStop.current();
        setPending({ op: "stop", meeting: current, remote: latest });
      })();
      return;
    }
    showStopped(latest, roleRef.current === "follower" ? "another-tab" : "another-device");
    // `dataUpdatedAt`: an unchanged answer still re-checks the recording tab.
  }, [status.data, status.dataUpdatedAt, placeRecording, showStopped, flushNotes]);
  const statusError = status.error;
  useEffect(() => {
    // Deleted elsewhere: nothing left to show.
    if ((statusError as { status?: number } | null)?.status !== 404 || roleRef.current === "recorder") return;
    setMeeting(null);
    setRole(null);
    setPhase("rest");
  }, [statusError]);
  useRefreshSuggestionsOnNotes(after, after ? meeting?.status : undefined);

  useEffect(() => {
    if (!stopAsked) return;
    const timer = setTimeout(() => setStopAsked(false), STOP_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [stopAsked]);

  const voiceStop = voice.stop;
  const voiceOn = voice.on;
  const live = phase === "recording" || phase === "interrupted" || phase === "starting" || phase === "stopping";
  const setMode = useCallback(
    (next: "personal" | "meeting") => {
      // Entering Meeting mode ends a Talk call (the effect below).
      if (next === "meeting") {
        setModeState("meeting");
        return;
      }
      // Leaving Meeting mode while recording is a Stop; it asks first.
      if (live) {
        setStopAsked(true);
        return;
      }
      setModeState("personal");
    },
    [live, setModeState],
  );
  // Talk and Meeting never run together: entering Meeting mode from another tab, or by following a
  // recording, ends a Talk call here too. Only on entering it, so voice started later is left alone.
  const effectiveMode = live ? "meeting" : mode;
  const wasMeeting = useRef(effectiveMode === "meeting");
  useEffect(() => {
    const entered = effectiveMode === "meeting" && !wasMeeting.current;
    wasMeeting.current = effectiveMode === "meeting";
    if (entered && voiceOn) voiceStop();
  }, [effectiveMode, voiceOn, voiceStop]);
  // Personal, chosen here or in another tab, clears a finished meeting; a live one keeps Meeting mode
  // until it ends, and is cleared then.
  useEffect(() => {
    if (mode !== "personal" || live) return;
    setPhase("rest");
    setMeeting(null);
    setError(null);
    setStoppedBy(null);
  }, [mode, live]);

  // Notes Stop had to cancel; their words stay on screen after Stop so they can be copied.
  const unsavedNotes = useRef<UnsavedNote[]>([]);
  // Notes that didn't save, so the same words typed again retry the note rather than start a new one:
  // the note box keeps its last failed note, and a failed Tell Kory asks for its words to be typed.
  // Only a typed note retries; a held Tell Kory window is always a new note.
  const failedTyped = useRef<UnsavedNote | null>(null);
  const failedTold = useRef<{ note: UnsavedNote; words: string }[]>([]);
  const finish = useCallback(
    (latest: Meeting, warning: string | null) => {
      const lost = takeLostNotes();
      setMeeting(latest);
      setRole(null);
      setStoppedBy(null);
      setPhase("after");
      setError([warning, lost].filter(Boolean).join(" ") || null);
      void queryClient.invalidateQueries({ queryKey: ["meetings"] });
      tellTabs("stopped", latest.id);
    },
    [queryClient, takeLostNotes],
  );

  // Capture has stopped the meeting; a failed read of it only delays the status, which the notes poll picks up.
  const finishAfterCapture = useCallback(
    async (current: Meeting, warning: string | null) =>
      finish(await getMeeting(current.id).catch(() => ({ ...current, status: "processing" as const })), warning),
    [finish],
  );

  // No capture to stop it (after a reload), or capture's stop failed: stop the meeting here.
  const serverStop = useCallback(
    async (current: Meeting, warning: string | null) => {
      try {
        let latest: Meeting = await getMeeting(current.id);
        if (latest.status === "recording") latest = await stopMeeting(current.id);
        finish(latest, warning);
      } catch (err) {
        setError(apiErrorMessage(err, "Couldn't stop the meeting. Try again."));
        setPhase(roleRef.current === "follower" ? "recording" : "interrupted");
      }
    },
    [finish],
  );

  useEffect(() => {
    const status = capture.status;
    if (pending?.op === "start") {
      if (status === "starting") return;
      setPending(null);
      if (pending.followed) {
        // Another tab records this meeting: follow it, and leave the meeting recording.
        setError(null);
        setRole("follower");
        setPhase("recording");
        return;
      }
      if (status === "recording") {
        setPhase("recording");
        setRole("recorder");
        if (meetingRef.current) tellTabs("started", meetingRef.current.id);
        return;
      }
      releaseLock();
      // `idle`: the share picker was cancelled, which is not an error. `error`: media or the meeting failed.
      const failed = status === "error";
      if (pending.kind === "resume") {
        if (failed) setError(capture.error ?? MEDIA_FAILED);
        setPhase("interrupted");
        return;
      }
      // Nothing was heard: end the empty meeting rather than leave it recording.
      if (pending.created) void stopMeeting(pending.created.id).catch(() => {});
      setMeeting(null);
      if (failed) {
        setError(
          pending.createError !== undefined
            ? apiErrorMessage(pending.createError, "Couldn't start the meeting. Try again.")
            : (capture.error ?? MEDIA_FAILED),
        );
      }
      // The runner's Record has no sheet to go back to; its error shows on the unit at rest.
      setPhase(pending.kind === "team" ? "rest" : "sheet");
      return;
    }
    if (pending?.op === "stop") {
      if (status === "stopping") return;
      setPending(null);
      if (pending.remote) {
        releaseLock();
        const lost = takeLostNotes();
        showStopped(pending.remote, "another-device");
        if (lost) setError(lost);
        return;
      }
      if (status === "stopped") {
        releaseLock();
        void finishAfterCapture(pending.meeting, capture.error);
        return;
      }
      // Capture couldn't stop the meeting: stop it here, holding the lock until that settles so no
      // other tab resumes a meeting this tab is stopping.
      setRole(null);
      void serverStop(pending.meeting, capture.error ? `Kory couldn't save all of the recording: ${capture.error}` : LOST_TAIL).finally(
        releaseLock,
      );
      return;
    }
    // Capture ended by itself (the mic went away): show it stopped. If capture couldn't stop the
    // meeting, stop it here; either way this tab stops being the recorder, so another can Resume.
    if (phaseRef.current !== "recording" || roleRef.current !== "recorder" || !meetingRef.current) return;
    if (status === "stopped") {
      releaseLock();
      void finishAfterCapture(meetingRef.current, capture.error);
    } else if (status === "error") {
      // The lock is kept until this stop settles, so no other tab resumes a meeting this tab is stopping.
      setRole(null);
      setPhase("stopping");
      void serverStop(meetingRef.current, capture.error ? `Kory couldn't save all of the recording: ${capture.error}` : LOST_TAIL).finally(
        releaseLock,
      );
    }
  }, [pending, capture.status, capture.error, finishAfterCapture, serverStop, releaseLock, showStopped, takeLostNotes]);

  // A tab that starts following, by any path, asks the recording tab whether it is paused.
  useEffect(() => {
    const id = meetingRef.current?.id;
    if (role === "follower" && id) tellTabs("sync-request", id);
  }, [role]);

  // The recording tab tells the others when it pauses or resumes.
  const captureStatus = capture.status;
  const captureStatusRef = useRef(captureStatus);
  captureStatusRef.current = captureStatus;
  useEffect(() => {
    const id = meetingRef.current?.id;
    if (role !== "recorder" || !id || (captureStatus !== "paused" && captureStatus !== "recording")) return;
    tellTabs("paused", id, { paused: captureStatus === "paused" });
  }, [role, captureStatus]);

  const begin = useCallback(
    async (body: StartMeetingBody) => {
      if (voiceOn) voiceStop();
      setError(null);
      setPhase("starting");
      // Capture asks for the mic and the tab first and creates the meeting only once they are granted,
      // so a cancelled share picker leaves no meeting behind.
      const outcome: StartPending = { op: "start", kind: body.kind, created: null, createError: undefined };
      const create = async () => {
        try {
          outcome.created = await startMeeting(body);
        } catch (err) {
          outcome.createError = err;
          throw err;
        }
        // This tab records it: the other tabs see the lock held and follow.
        const release = await acquireTabLock(recorderLock(outcome.created.id));
        if (release && !mounted.current) {
          release();
          throw new Error("The tray closed");
        }
        setMeeting(outcome.created);
        if (!release) {
          // Another tab already resumed it: no second recorder. Capture gives up the mic and tab.
          outcome.followed = true;
          throw new Error("Recording in another tab");
        }
        lockRef.current = { id: outcome.created.id, release };
        return outcome.created;
      };
      await capture.start({ meeting: create, inRoom: body.in_room });
      setPending({ ...outcome });
    },
    [capture, voiceOn, voiceStop],
  );

  const start = useCallback(
    ({ title, inRoom }: { title: string; inRoom: InRoom }) =>
      begin({ kind: "adhoc", in_room: inRoom, ...(title.trim() ? { title: title.trim() } : {}) }),
    [begin],
  );

  const startTeamMeeting = useCallback(
    async ({ teamId, l10SessionId, title, inRoom }: { teamId: string; l10SessionId: string; title?: string; inRoom: InRoom }) => {
      if (meetingRef.current && phaseRef.current !== "after") return;
      setStoppedBy(null);
      setModeState("meeting");
      await begin({
        kind: "team",
        team_id: teamId,
        l10_session_id: l10SessionId,
        in_room: inRoom,
        ...(title?.trim() ? { title: title.trim() } : {}),
      });
    },
    [begin, setModeState],
  );

  // The last anchor, so a typed note or a Tell Kory ask lands under the item being discussed.
  const anchorRef = useRef<MeetingAnchor>(null);
  const captureSetAnchor = capture.setAnchor;
  const setAnchor = useCallback(
    (next: MeetingAnchor) => {
      anchorRef.current = next;
      captureSetAnchor(next);
    },
    [captureSetAnchor],
  );

  // The tray owns the notes and the room it edits, so a copy fetched before a save can't roll them back.
  const adoptMeeting = useCallback((next: Meeting) => {
    setMeeting((m) => (m && m.id === next.id ? { ...next, notes_md: m.notes_md, in_room: m.in_room } : m));
  }, []);

  const resumeRecording = useCallback(async () => {
    const current = meetingRef.current;
    if (!current) return;
    setError(null);
    setPhase("starting");
    // Another tab resumed it first: follow that tab rather than record twice.
    const release = await acquireTabLock(recorderLock(current.id));
    if (release && !mounted.current) {
      release();
      return;
    }
    if (!release) {
      setRole("follower");
      setPhase("recording");
      return;
    }
    lockRef.current = { id: current.id, release };
    await capture.start({ meeting: current.id, inRoom: current.in_room });
    setPending({ op: "start", kind: "resume", created: null, createError: undefined });
  }, [capture]);

  const stop = useCallback(async () => {
    const current = meetingRef.current;
    if (!current) return;
    setStopAsked(false);
    setError(null);
    if (roleRef.current === "follower") {
      // The recording tab stops it, flushing what it heard; if it doesn't answer, stop it here.
      setPhase("stopping");
      stopRelay.current = current.id;
      tellTabs("stop-request", current.id);
      armStopFallbackRef.current(current, STOP_RELAY_MS);
      return;
    }
    const capturing = phase !== "interrupted";
    setPhase("stopping");
    await flushNotes(current.id);
    if (!capturing) {
      await serverStop(current, null);
      return;
    }
    // Capture's stop flushes the last text and audio, then stops the meeting.
    await capture.stop();
    setPending({ op: "stop", meeting: current });
  }, [capture, phase, serverStop, flushNotes]);
  // The meeting a follower's Stop asked the recording tab to stop, until that tab says it stopped,
  // and the server stop that runs if it never does.
  const stopRelay = useRef<string | null>(null);
  const stopFallback = useRef<ReturnType<typeof setTimeout> | null>(null);
  function armStopFallback(current: Meeting, ms: number) {
    if (stopFallback.current) clearTimeout(stopFallback.current);
    stopFallback.current = setTimeout(() => {
      if (stopRelay.current !== current.id) return;
      stopRelay.current = null;
      void serverStop(current, null);
    }, ms);
  }
  const armStopFallbackRef = useRef(armStopFallback);
  armStopFallbackRef.current = armStopFallback;
  useEffect(
    () => () => {
      if (stopFallback.current) clearTimeout(stopFallback.current);
    },
    [],
  );
  const stopRef = useRef(stop);
  stopRef.current = stop;

  const done = useCallback((tell = true) => {
    const id = meetingRef.current?.id;
    if (id) dismissed.current = id;
    if (tell && id && phaseRef.current === "after") tellTabs("done", id);
    setMeeting(null);
    setStoppedBy(null);
    setPhase("rest");
  }, []);

  useEffect(
    () =>
      onTrayMessage<MeetingMessage>("meeting", (msg) => {
        const current = meetingRef.current;
        const mine = current?.id === msg.meetingId;
        const p = phaseRef.current;
        const r = roleRef.current;
        if (msg.event === "started") {
          if (!mine || p === "interrupted") void getMeeting(msg.meetingId).then(placeRecording, () => undefined);
        } else if (msg.event === "paused") {
          if (mine && r === "follower") setRemotePaused(Boolean(msg.paused));
        } else if (msg.event === "stopped") {
          if (!current || !mine || r === "recorder" || !(p === "recording" || p === "interrupted" || p === "stopping")) return;
          const asked = stopRelay.current === msg.meetingId;
          stopRelay.current = null;
          if (stopFallback.current) clearTimeout(stopFallback.current);
          void getMeeting(msg.meetingId)
            .catch(() => ({ ...current, status: "processing" as const }))
            .then((latest) => {
              // Landed after this tab resumed recording it: leave the recording alone (if it really
              // stopped, the recorder's own status check ends capture properly).
              if (roleRef.current === "recorder" || phaseRef.current === "starting") return;
              showStopped(latest, asked ? null : "another-tab");
            });
        } else if (msg.event === "done") {
          // Closed elsewhere, maybe before this tab's read of the stop lands.
          dismissed.current = msg.meetingId;
          if (mine && p === "after") done(false);
        } else if (msg.event === "stop-request") {
          if (!mine || r !== "recorder" || p !== "recording") return;
          tellTabs("stopping", msg.meetingId);
          void stopRef.current();
        } else if (msg.event === "stopping") {
          // The recording tab is flushing: give it its full save window before stopping here.
          if (current && mine && stopRelay.current === msg.meetingId) armStopFallbackRef.current(current, STOP_ACKED_MS);
        } else if (msg.event === "sync-request") {
          const st = captureStatusRef.current;
          if (mine && r === "recorder" && (st === "paused" || st === "recording")) {
            tellTabs("paused", msg.meetingId, { paused: st === "paused" });
          }
        }
      }),
    [placeRecording, showStopped, done],
  );

  // A meeting's room writes go to the server one at a time, in order, and so do its notes writes, so
  // a reply never lands out of order and each save builds on the last one that landed.
  const queueWrite = useCallback(<T,>(current: Meeting, field: "room" | "notes", write: (w: Writes) => Promise<T>) => {
    if (writes.current?.id !== current.id) {
      const chains = { room: Promise.resolve() as Promise<unknown>, notes: Promise.resolve() as Promise<unknown> };
      writes.current = { id: current.id, chains, room: current.in_room, notes: current.notes_md, noteSaves: new Map() };
    }
    const w = writes.current;
    const next = w.chains[field].then(() => write(w));
    w.chains[field] = next.catch(() => {});
    return next;
  }, []);

  const setInRoom = useCallback(
    (inRoom: InRoom) => {
      const current = meetingRef.current;
      if (!current || current.in_room === inRoom) return;
      // Only the room changes here, so a note saved meanwhile stays. If the newest choice fails, the
      // room goes back to the last one the server saved, never to an unsaved choice.
      const room = (value: InRoom) => setMeeting((m) => (m && m.id === current.id ? { ...m, in_room: value } : m));
      const seq = ++roomSeq.current;
      room(inRoom);
      capture.setInRoom(inRoom);
      void queueWrite(current, "room", async (w) => {
        try {
          await patchMeeting(current.id, { in_room: inRoom });
          w.room = inRoom;
        } catch {
          if (seq !== roomSeq.current) return;
          room(w.room);
          capture.setInRoom(w.room);
        }
      });
    },
    [capture, queueWrite],
  );

  const addNote = useCallback(
    async (text: string, ask?: NoteAsk) => {
      const current = meetingRef.current;
      // Once Stop starts, notes are closed: Kory composes them from what has landed.
      if (!current || !text.trim() || phaseRef.current === "stopping" || phaseRef.current === "after") return false;
      const line = noteLine(text, current.started_at, new Date());
      const typed = (ask?.source ?? "typed") === "typed";
      const retry = !typed
        ? null
        : failedTyped.current?.text === text.trim()
          ? failedTyped.current
          : failedTold.current.find((f) => f.words === text.trim())?.note;
      const note: UnsavedNote = { id: retry?.id ?? crypto.randomUUID(), text: text.trim() };
      const anchor = anchorRef.current;
      return queueWrite(current, "notes", async (w) => {
        const save = new AbortController();
        w.noteSaves.set(save, note);
        try {
          const notes = w.notes ? `${w.notes}\n${line}` : line;
          const saved = await patchMeeting(current.id, { notes_md: notes }, save.signal);
          w.notes = saved.notes_md;
          // Only a note that made it into the meeting is offered as an ask.
          sendAsk(current.id, ask ?? { source: "typed", text: text.trim() }, anchor);
          // Saved on a retry after a cancelled Stop: no longer one to copy.
          unsavedNotes.current = unsavedNotes.current.filter((n) => n.id !== note.id);
          if (typed) {
            failedTyped.current = null;
            failedTold.current = failedTold.current.filter((f) => f.note.id !== note.id);
          }
          setMeeting((m) => (m && m.id === saved.id ? { ...m, notes_md: saved.notes_md, updated_at: saved.updated_at } : m));
          return true;
        } catch {
          if (typed) failedTyped.current = note;
          else failedTold.current = [...failedTold.current, { note, words: (ask?.text ?? text).trim() }];
          return false;
        } finally {
          w.noteSaves.delete(save);
        }
      });
    },
    [queueWrite],
  );

  const value = useMemo<MeetingMode>(
    () => ({
      capable,
      mode: !capable ? "personal" : live ? "meeting" : mode,
      setMode,
      phase,
      live,
      role: phase === "recording" || phase === "stopping" ? role : null,
      paused: role === "follower" ? remotePaused : capture.status === "paused",
      stoppedBy: phase === "after" ? stoppedBy : null,
      meeting,
      capture,
      error,
      openSheet: () => {
        setError(null);
        setPhase((p) => (p === "rest" ? "sheet" : p));
      },
      closeSheet: () => setPhase((p) => (p === "sheet" ? "rest" : p)),
      start,
      startTeamMeeting,
      setAnchor,
      adoptMeeting,
      resumeRecording,
      stopAsked,
      askStop: () => setStopAsked(true),
      cancelStop: () => setStopAsked(false),
      stop,
      setInRoom,
      addNote,
      done: () => done(),
    }),
    [capable, mode, setMode, phase, live, role, remotePaused, stoppedBy, meeting, capture, error, start, startTeamMeeting, setAnchor, adoptMeeting, resumeRecording, stopAsked, stop, setInRoom, addNote, done],
  );
  return <MeetingModeContext.Provider value={value}>{children}</MeetingModeContext.Provider>;
}

/** The meeting's suggestions waiting on someone; one poll serves the rail's count and the tray's list. */
export function useWaitingSuggestions(meetingId: string | null, live: boolean) {
  return useQuery({
    queryKey: ["meetings", meetingId, "suggestions", "suggested"],
    queryFn: async (): Promise<MeetingSuggestion[]> =>
      (await listSuggestions(meetingId as string, { state: "suggested", limit: 200 })).suggestions,
    enabled: Boolean(meetingId),
    refetchInterval: live ? 5_000 : false,
  });
}
